import React from 'react';
import { AppContextValue } from '@prisjakt/ui-app-context';
import * as H from 'history';

export class ErrorBoundaryInner extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public unlisten: H.UnregisterCallback | undefined;

  public context: AppContextValue;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
    };
    this.context = props.context;
  }

  componentDidMount() {
    this.unlisten = this.props.history.listen(location => {
      if (this.state.hasError && this.state.pathname !== location.pathname) {
        this.setState({
          hasError: false,
        });
      }
    });
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const { captureException } = this.context;

    import(
      /* webpackChunkName: "sourcemapped-stacktrace" */ 'sourcemapped-stacktrace'
    )
      .then(({ mapStackTrace }) => {
        mapStackTrace(error.stack, mappedStack => {
          error.stack = mappedStack.join('\n'); // eslint-disable-line no-param-reassign
          captureException({ ...error, extra: info });
        });
      })
      .catch(() => captureException({ ...error, extra: info }));

    this.setState({
      error,
      hasError: true,
      pathname: this.props.history.location.pathname,
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.onError(this.state.error || {}) || null;
    }

    return this.props.children;
  }
}

interface ErrorBoundaryProps {
  onError: (error: any) => void | React.ReactNode;
  children: React.ReactNode;
  history: H.History<unknown>;
  context: AppContextValue;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error | undefined;
  pathname?: string;
}
